/** The banner above the register when the year-on-year bridge opened it on a list of sites.
 *
 * Same pattern as Coverage's narrowing banner: a quiet strip above the tabs, one sentence saying
 * what is in force and how much of it, and one button back to the whole thing. The wording is
 * `siteNarrowingModel.ts`'s; this file only lays it out.
 */

import styles from "./Screen.module.css";
import own from "./Inventory.module.css";
import type { SiteBanner } from "./siteNarrowingModel";

export type ComparisonState = "ok" | "pending" | "failed";

/** Whether the figures the register narrows have been read for this window. **A claim about which
 * sites have a figure is only true once they have**: while the read is in flight the register holds
 * no sites at all, and a banner saying "none of these sites has a figure" would be the register's
 * silently empty state, said aloud and wrong. */
export type FiguresState = "ready" | "reading" | "failed";

export function SiteNarrowing({
  banner,
  comparison,
  figures,
  onShowEvery,
  onShowWindow,
  onRetry,
}: {
  banner: SiteBanner;
  /** Whether the comparison the figure is summed from could be read. Pending says nothing; failed
   * says so, because a sentence silently missing its figure reads as "nothing to report". */
  comparison: ComparisonState;
  figures: FiguresState;
  onShowEvery: () => void;
  onShowWindow: (from: string, to: string) => void;
  onRetry: () => void;
}) {
  const plural = banner.asked === 1 ? "site" : "sites";
  const verb = banner.asked === 1 ? "explains" : "explain";
  return (
    <div className={own.siteBanner} data-testid="site-narrowing" role="status">
      <p data-testid="site-narrowing-says">
        {figures !== "ready" ? (
          <>
            Narrowed to {banner.asked} {plural}
            {figures === "reading" ? "; reading their figures." : ". Their figures could not be read."}
          </>
        ) : banner.shown === 0 ? (
          <>
            None of the {banner.asked} {plural} has a figure in this window, so nothing is narrowed.
          </>
        ) : (
          <>
            Showing {banner.shown}
            {banner.shown < banner.asked ? ` of ${banner.asked}` : ""} {plural}
            {banner.sum !== undefined && banner.basis === "explained" ? (
              <>
                {" "}that {verb} <strong data-testid="site-narrowing-sum">{banner.sum}</strong> tCO₂e
                of the change against last year
              </>
            ) : banner.sum !== undefined && banner.basis === "unexplained" ? (
              <>
                {" "}behind <strong data-testid="site-narrowing-sum">{banner.sum}</strong> tCO₂e of the
                change that no boundary change explains
              </>
            ) : banner.sum !== undefined ? (
              <>
                {" "}behind <strong data-testid="site-narrowing-sum">{banner.sum}</strong> tCO₂e of the
                change against last year
              </>
            ) : null}
            .
          </>
        )}
        {figures === "ready" && banner.missing.length > 0 ? (
          <span data-testid="site-narrowing-missing">
            {" "}
            {banner.missing.join(", ")} {banner.missing.length === 1 ? "has" : "have"} no figure in
            this window.
          </span>
        ) : null}
        {comparison === "failed" ? (
          <span data-testid="site-narrowing-unread">
            {" "}
            What these sites explain could not be read.{" "}
            <button type="button" className={styles.fixLink} onClick={onRetry}>
              Try again
            </button>
          </span>
        ) : null}
      </p>
      {banner.other ? (
        <button
          type="button"
          className={styles.fixLink}
          data-testid="site-narrowing-window"
          onClick={() => {
            if (banner.other) onShowWindow(banner.other.from, banner.other.to);
          }}
        >
          Show {banner.other.which}&rsquo;s figures
        </button>
      ) : null}
      <button
        type="button"
        className={styles.fixLink}
        data-testid="site-narrowing-clear"
        onClick={onShowEvery}
      >
        Show every figure
      </button>
    </div>
  );
}
